import { supabase } from "@/integrations/supabase/client";
import { getCurrentUserId } from "@/lib/authCache";
import { isStaleTrial } from "@/lib/trialAccess";

export type AccessLevel = "full" | "grace" | "course_only" | "none" | "paused";

const ACTIVE_SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
  "pending_cancellation",
  // on_hold = card-payment failure. Full access is retained (a payment banner
  // nudges the fix); only a save-offer "paused" walls course content.
  "on_hold",
];

interface AccessInfo {
  level: AccessLevel;
  graceEndDate: string | null;
  hasCompletedCourse: boolean;
}

/**
 * Computes the end of the post-completion grace window for a PAID-entry
 * challenge: cohort `end_date` (interpreted as end-of-day Israel time) plus
 * `grace_period_days` (defaults to 7 when null/undefined).
 */
export function paidEntryGraceEnd(endDate: string, graceDays: number | null): Date {
  const end = new Date(`${endDate}T23:59:59+03:00`);
  end.setDate(end.getDate() + (graceDays ?? 7));
  return end;
}

/**
 * Reads `profiles.subscription_period_end` for the trial-staleness check.
 *
 * Returns null on ANY failure (network, RLS, missing row). Null means "not
 * stale" to `isStaleTrial`, so a failed read can never wall a member — the same
 * fail-open posture the rest of this file takes.
 */
async function fetchSubscriptionPeriodEnd(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("subscription_period_end")
      .eq("id", userId)
      .maybeSingle();
    if (error) return null;
    return (data as { subscription_period_end: string | null } | null)?.subscription_period_end ?? null;
  } catch {
    return null;
  }
}

/**
 * Computes the user's current access level based on subscription status
 * and enrollment state. Used by SubscriptionGate and nav gating.
 *
 * Priority:
 * 1. Active subscription → full
 * 2. Active (non-completed) enrollment → full (covers a LIVE paid-entry challenge)
 * 3. Completed course with grace_end_date in the future → grace
 * 4. Completed course with grace expired or null → course_only
 * 5. Completed PAID-entry challenge within end_date + grace_period_days → grace
 * 6. Completed PAID-entry challenge past grace, with a user_course_access grant → course_only
 * 7. Nothing → none
 */
export async function getUserAccessInfo(
  subscriptionStatus: string | null | undefined,
  // When set (admin "view as user"), enrollment/course-access lookups run for
  // THIS user id instead of the current session's. Admin RLS permits reading
  // other users' enrollment rows. Callers must also pass the target's
  // subscriptionStatus so the whole computation reflects the viewed-as user.
  overrideUserId?: string
): Promise<AccessInfo> {
  if (
    subscriptionStatus &&
    ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus)
  ) {
    // `trialing` is the one active status that can outlive its own truth: the
    // trial either converted or lapsed, and a profile still reading `trialing`
    // long after its period end is a missed webhook, not access. Every other
    // status renews, so its period end being in the past is normal.
    //
    // Fetched here rather than plumbed through the caller deliberately — the
    // auth user object doesn't carry the column, and widening the boot state to
    // add it puts this on the fail-closed boot latch, which is its own lockout
    // class. The extra read only happens for trialing profiles.
    if (subscriptionStatus !== "trialing") {
      return { level: "full", graceEndDate: null, hasCompletedCourse: false };
    }

    const trialUserId = overrideUserId ?? await getCurrentUserId();
    const trialPeriodEnd = trialUserId ? await fetchSubscriptionPeriodEnd(trialUserId) : null;

    if (!isStaleTrial(subscriptionStatus, trialPeriodEnd)) {
      return { level: "full", graceEndDate: null, hasCompletedCourse: false };
    }
    // Stale: fall through to the enrollment/course checks below. This is NOT a
    // denial — a trial buyer enrolled in a live entry challenge still resolves
    // to "full" a few lines down. It only removes the unconditional shortcut.
  }

  // Paused membership (save-offer pause): short-circuit before any DB query —
  // mirrors computeAccessLevel. Community access is granted at the auth/session
  // layer; course content is gated via the PausedCourseWall. No enrollment
  // queries needed. (Card-failure on_hold is handled above as full access.)
  if (subscriptionStatus === "paused") {
    return { level: "paused", graceEndDate: null, hasCompletedCourse: false };
  }

  const userId = overrideUserId ?? await getCurrentUserId();
  if (!userId) return { level: "none", graceEndDate: null, hasCompletedCourse: false };

  // Active enrollments grouped by challenge type. Course-only buyers (e.g.
  // FunnelKit purchases) hold an enrollment in a course-type challenge but
  // have no active subscription — they should see only the course, not the
  // rest of the app. Active enrollment in a non-course challenge (entry, …)
  // implies subscriber-level access.
  //
  // The "community" challenge is EXCLUDED: it is a subscriber benefit (its
  // price is an active subscription), not standalone access, so a community
  // enrollment must NOT grant access on its own once the subscription lapses.
  // This mirrors getProtectivePaidContent (sy-subscription-webhook), which
  // also excludes community — keeping the access decision in sync across the
  // two systems.
  let hasActiveNonCourseEnrollment = false;
  let hasActiveCourseEnrollment = false;

  const { data: actives, error: enrollError } = await supabase
    .from("user_challenge_enrollments")
    .select("id, challenges!inner(challenge_type)")
    .eq("user_id", userId)
    .is("completed_at", null)
    .is("queued_at", null);

  if (enrollError) {
    // Fallback: queued_at column missing or join failed. Treat any active
    // enrollment as full to avoid blocking users due to a schema mismatch.
    const { data: fallback } = await supabase
      .from("user_challenge_enrollments")
      .select("id")
      .eq("user_id", userId)
      .is("completed_at", null)
      .limit(1)
      .maybeSingle();
    hasActiveNonCourseEnrollment = !!fallback;
  } else if (actives) {
    for (const enrollment of actives) {
      const challenge = (enrollment as { challenges?: { challenge_type?: string } }).challenges;
      const type = challenge?.challenge_type;
      if (type === "course") {
        hasActiveCourseEnrollment = true;
      } else if (type && type !== "community") {
        hasActiveNonCourseEnrollment = true;
      }
    }
  }

  if (hasActiveNonCourseEnrollment) {
    return { level: "full", graceEndDate: null, hasCompletedCourse: false };
  }
  if (hasActiveCourseEnrollment) {
    return { level: "course_only", graceEndDate: null, hasCompletedCourse: false };
  }

  // Check for completed course with grace period
  const { data: completedCourse } = await supabase
    .from("user_challenge_enrollments")
    .select("grace_end_date")
    .eq("user_id", userId)
    .eq("is_course_completed", true)
    .order("completed_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (completedCourse) {
    const graceEnd = completedCourse.grace_end_date;
    if (graceEnd && new Date(graceEnd) >= new Date()) {
      return { level: "grace", graceEndDate: graceEnd, hasCompletedCourse: true };
    }
    return { level: "course_only", graceEndDate: graceEnd, hasCompletedCourse: true };
  }

  // Completed PAID entry challenge => grace window of full access, then floor (spec §5.7)
  const { data: paidCompleted } = await supabase
    .from("user_challenge_enrollments")
    .select("completed_at, challenges!inner(end_date, grace_period_days, paid_entry)")
    .eq("user_id", userId)
    .eq("challenges.paid_entry", true)
    .not("completed_at", "is", null)
    .order("completed_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (paidCompleted) {
    const ch = (paidCompleted as { challenges?: { end_date?: string; grace_period_days?: number | null } }).challenges;
    if (ch?.end_date) {
      const graceEnd = paidEntryGraceEnd(ch.end_date, ch.grace_period_days ?? null);
      if (graceEnd >= new Date()) {
        return { level: "grace", graceEndDate: graceEnd.toISOString(), hasCompletedCourse: false };
      }
    }
  }

  // user_course_access without an enrollment — treat as course_only.
  // (Previously returned "full"; tightened so course-only buyers without
  // an active enrollment row don't get full app access.)
  const { data: wooAccess } = await supabase
    .from("user_course_access")
    .select("id")
    .eq("user_id", userId)
    .eq("is_active", true)
    .limit(1)
    .maybeSingle();

  if (wooAccess) {
    return { level: "course_only", graceEndDate: null, hasCompletedCourse: false };
  }

  return { level: "none", graceEndDate: null, hasCompletedCourse: false };
}

/**
 * Synchronous access level check from known state (no DB calls).
 * Use this when you already have the subscription status and enrollment info.
 *
 * `hasActiveNonCourseEnrollment`: true when the user has any non-completed,
 * non-queued enrollment in an entry/community-type challenge — implies
 * subscriber-level access (legacy or admin-granted).
 * `hasActiveCourseEnrollment`: true when the user has any non-completed,
 * non-queued enrollment in a course-type challenge — by itself, this only
 * grants course_only access.
 */
export function computeAccessLevel(
  subscriptionStatus: string | null | undefined,
  hasActiveNonCourseEnrollment: boolean,
  hasActiveCourseEnrollment: boolean,
  completedCourseGraceEnd: string | null,
  // Optional so every existing call keeps its exact behaviour. Present only to
  // stop this function drifting from getUserAccessInfo, which now applies the
  // same trial-staleness rule — two functions that claim to compute the same
  // access level must not disagree about a trialing member.
  subscriptionPeriodEnd?: string | null
): AccessLevel {
  // Paused membership (save-offer pause): community granted (handled by callers),
  // course content locked elsewhere via is_active=false. Distinct level so the UI
  // can show a paused wall. (Card-failure on_hold falls through to "full" below.)
  if (subscriptionStatus === "paused") return "paused";

  if (
    subscriptionStatus &&
    ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus) &&
    !isStaleTrial(subscriptionStatus, subscriptionPeriodEnd)
  ) {
    return "full";
  }
  if (hasActiveNonCourseEnrollment) return "full";
  if (hasActiveCourseEnrollment) return "course_only";
  if (completedCourseGraceEnd) {
    return new Date(completedCourseGraceEnd) >= new Date() ? "grace" : "course_only";
  }
  return "none";
}
